import React, { useState, useEffect, useMemo } from 'react';
import CharacterDataNormalizer from '../utils/CharacterDataNormalizer';
import { logIO } from '../utils/DebugLogger';

/**
 * Test component for verifying character data consistency
 * between edit mode and view mode
 */
const CharacterConsistencyTest = () => {
  const [testResults, setTestResults] = useState([]);
  const [summary, setSummary] = useState({ passed: 0, total: 0 });

  // Define test scenarios (memoized to satisfy react-hooks/exhaustive-deps)
  const testScenarios = useMemo(() => [
    {
      name: "Basic Fighter Character",
      editModeData: {
        id: 'fighter-1',
        name: 'Thalion the Bold',
        class: 'Fighter',
        level: 5,
        abilities: {
          strength: 16,
          dexterity: 14, 
          constitution: 15,
          intelligence: 10,
          wisdom: 12,
          charisma: 8
        },
        hp: 45,
        maxHp: 45,
        ac: 16
      },
      expectedViewData: {
        name: 'Thalion the Bold',
        class: 'Fighter',
        level: 5,
        hp: 45,
        maxHp: 45,
        ac: 16
      }
    },
    {
      name: "Wizard with Stats Format",
      editModeData: {
        id: 'wizard-1',
        name: 'Elminster',
        class: 'Wizard',
        level: 10,
        stats: {
          str: 8,
          dex: 14,
          con: 12,
          int: 18,
          wis: 14,
          cha: 10
        },
        hp: 60,
        maxHp: 60,
        ac: 12
      },
      expectedViewData: {
        name: 'Elminster',
        class: 'Wizard',
        level: 10,
        hp: 60,
        maxHp: 60,
        ac: 12
      }
    },
    {
      name: "Mixed Data Format",
      editModeData: {
        id: 'mixed-1',
        name: 'Hybrid Character',
        class: 'Paladin',
        level: 7,
        // Mixed format - some in abilities, some direct
        abilities: {
          strength: 16,
          dexterity: 12
        },
        constitution: 14,
        intelligence: 10,
        wisdom: 13,
        charisma: 14,
        hp: 65,
        maxHp: 70,
        ac: 18
      },
      expectedViewData: {
        name: 'Hybrid Character',
        class: 'Paladin',
        level: 7,
        hp: 65,
        maxHp: 70,
        ac: 18
      }
    },
    {
      name: "Character with spellcasting",
      editModeData: {
        id: 'spellcaster-1',
        name: 'Merlin',
        class: 'Wizard',
        level: 15,
        abilities: {
          strength: 8,
          dexterity: 14,
          constitution: 12,
          intelligence: 20,
          wisdom: 16,
          charisma: 12
        },
        hp: 90,
        maxHp: 90,
        ac: 14,
        spellcasting: {
          ability: 'intelligence',
          level: 15,
          slots: {
            1: { used: 0, total: 4 },
            2: { used: 0, total: 3 },
            3: { used: 0, total: 3 },
            4: { used: 0, total: 3 },
            5: { used: 0, total: 2 },
            6: { used: 0, total: 1 },
            7: { used: 0, total: 1 },
            8: { used: 0, total: 1 }
          }
        },
        spells: ['Fireball', 'Magic Missile', 'Shield', 'Wish']
      },
      expectedViewData: {
        name: 'Merlin',
        class: 'Wizard',
        level: 15,
        hp: 90,
        maxHp: 90,
        ac: 14
      }
    }
  ], []);

  // Run tests
  useEffect(() => {
    const results = [];
    let passedCount = 0;
    
    testScenarios.forEach((scenario, index) => {
      const editData = scenario.editModeData;
      const expected = scenario.expectedViewData;
      
      // Normalize as would happen in CharacterEditor's preview mode
      const normalizedData = CharacterDataNormalizer.normalize(editData);
      logIO(`[Test] Normalized data for ${scenario.name}:`, normalizedData);
      
      // Check fields
      const fieldResults = [];
      let allMatch = true;
      
      // Check basic fields
      ['name', 'class', 'level', 'hp', 'maxHp', 'ac'].forEach(field => {
        if (expected[field] !== undefined) {
          const match = normalizedData[field] === expected[field];
          fieldResults.push({
            field,
            editValue: editData[field],
            normalizedValue: normalizedData[field],
            expectedValue: expected[field],
            match
          });
          
          if (!match) allMatch = false;
        }
      });
      
      // Check ability scores
      if (expected.stats) {
        const statsKeys = Object.keys(expected.stats);
        statsKeys.forEach(stat => {
          const match = normalizedData.stats[stat] === expected.stats[stat];
          fieldResults.push({
            field: `stats.${stat}`,
            editValue: editData.stats?.[stat] || editData.abilities?.[stat === 'str' ? 'strength' : stat === 'dex' ? 'dexterity' : stat === 'con' ? 'constitution' : stat === 'int' ? 'intelligence' : stat === 'wis' ? 'wisdom' : 'charisma'],
            normalizedValue: normalizedData.stats[stat],
            expectedValue: expected.stats[stat],
            match
          });
          
          if (!match) allMatch = false;
        });
      }
      
      if (allMatch) passedCount++;
      
      results.push({
        scenarioName: scenario.name,
        passed: allMatch,
        details: fieldResults
      });
    });
    
    setTestResults(results);
    setSummary({
      passed: passedCount,
      total: testScenarios.length,
      percentage: Math.round((passedCount / testScenarios.length) * 100)
    });
    
  }, [testScenarios]);

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h1>Character Data Consistency Test</h1>
      <p>This test verifies that character data is consistently normalized between edit and view modes.</p>
      
      <div style={{ 
        marginBottom: '20px', 
        padding: '15px', 
        borderRadius: '5px', 
        background: summary.passed === summary.total ? '#e6ffe6' : '#ffe6e6',
        border: `1px solid ${summary.passed === summary.total ? '#4CAF50' : '#f44336'}`
      }}>
        <h2>Summary</h2>
        <p style={{ fontSize: '1.2em' }}>
          <strong>Passed:</strong> {summary.passed}/{summary.total} tests ({summary.percentage}%)
        </p>
        {summary.passed === summary.total ? (
          <p style={{ color: '#4CAF50' }}>
            ✅ SUCCESS: Character data is consistently normalized between edit and view modes!
          </p>
        ) : (
          <p style={{ color: '#f44336' }}>
            ❌ FAILURE: Character data normalization has inconsistencies.
          </p>
        )}
      </div>
      
      <h2>Test Results</h2>
      {testResults.map((result, idx) => (
        <div key={idx} style={{ 
          marginBottom: '20px', 
          padding: '15px', 
          borderRadius: '5px',
          background: result.passed ? '#f0f8ff' : '#fff0f0',
          border: `1px solid ${result.passed ? '#2196F3' : '#f44336'}`
        }}>
          <h3>
            Test #{idx+1}: {result.scenarioName} {result.passed ? '✅' : '❌'}
          </h3>
          
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Field</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Edit Value</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Normalized Value</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Expected Value</th>
                <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Match</th>
              </tr>
            </thead>
            <tbody>
              {result.details.map((detail, detailIdx) => (
                <tr key={detailIdx} style={{ 
                  background: detail.match ? 'white' : '#fff0f0' 
                }}>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{detail.field}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{JSON.stringify(detail.editValue)}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{JSON.stringify(detail.normalizedValue)}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>{JSON.stringify(detail.expectedValue)}</td>
                  <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>
                    {detail.match ? '✅' : '❌'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default CharacterConsistencyTest;
